import { useState } from 'react';
import { LogIn, RefreshCw } from 'lucide-react';
import { useCentralita, cambiarEstadoAgente, reintentarSesion, URL_ACCESO } from '@/hooks/useCentralita';

/**
 * El estado del agente en la centralita.
 *
 * Lo que decide el estado es solo si te ENTRAN llamadas, no si puedes hacerlas.
 * Marcar se puede desde cualquiera, Offline incluido; AWS lo dice tal cual:
 * «Technically, agents can make an outbound call when their CCP is set to
 * Offline». Available es el único que además te mete en la cola de entrantes.
 *
 * La diferencia entre Offline y un estado personalizado no está en lo que
 * permiten —permiten lo mismo—, sino en lo que cuentan: en los informes,
 * «Offline» es no estar trabajando y un estado propio dice en qué se emplea ese
 * tiempo. De ahí que exista «Solo salientes».
 *
 * Connect tampoco cambia el estado por su cuenta al marcar: sigues en el que
 * elegiste durante la llamada y después.
 */

const EXPLICACION: Record<string, string> = {
  'Solo salientes': 'Puedes llamar y no te entran llamadas. Igual que Offline, pero en los informes se ve que estabas trabajando.',
  Available: 'Puedes llamar, y además te entran las llamadas de la centralita.',
  Offline: 'Puedes llamar; no te entran llamadas.',
  Break: 'En pausa. Puedes llamar; no te entran llamadas.',
};

const COLOR: Record<string, string> = {
  'Solo salientes': 'bg-sky-500',
  Available: 'bg-emerald-500',
  Offline: 'bg-slate-400',
  Break: 'bg-amber-400',
};

export default function EstadoAgente() {
  const { estadoAgente, estadosDisponibles, estado } = useCentralita();
  // El segundo botón solo aparece cuando ya se ha ido a identificarse: antes de
  // eso no significa nada y solo añade ruido.
  const [vuelto, setVuelto] = useState(false);

  if (estado === 'sin-sesion') {
    return (
      <div className="card py-4">
        <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">
          Entra en la centralita
        </p>
        <p className="text-sm text-slate-500 mt-1">
          Amazon Connect tiene su propia contraseña, distinta de la del CRM. Se abrirá en una ventana
          aparte y aguanta doce horas: basta con hacerlo una vez al día.
        </p>
        {/* Un enlace y no un botón con window.open: la ventana automática de
            Streams sale cinco segundos tarde y desde un callback, así que el
            navegador la bloquea siempre, y una abierta por script puede
            bloquearse también. Una pestaña abierta desde un enlace, nunca. */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <a
            href={URL_ACCESO}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setVuelto(true)}
            className="btn-primary flex items-center gap-2"
          >
            <LogIn size={16} /> Iniciar sesión
          </a>
          {vuelto && (
            <button onClick={reintentarSesion} className="btn-secondary flex items-center gap-2">
              <RefreshCw size={15} /> Ya he entrado
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!estadoAgente) {
    return (
      <div className="card py-3">
        <p className="text-sm text-slate-500">Esperando a la centralita…</p>
      </div>
    );
  }

  return (
    <div className="card py-3">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${COLOR[estadoAgente] ?? 'bg-amber-400'}`} />
        <select
          className="input py-1.5 flex-1 min-w-[140px]"
          value={estadoAgente}
          onChange={(e) => cambiarEstadoAgente(e.target.value)}
        >
          {/* Si Connect aún no ha mandado la lista, al menos se ve el actual. */}
          {(estadosDisponibles.length ? estadosDisponibles : [estadoAgente]).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      {EXPLICACION[estadoAgente] && (
        <p className="text-xs text-slate-400 mt-2">{EXPLICACION[estadoAgente]}</p>
      )}
    </div>
  );
}
